import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import swal from "sweetalert";

import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import SimpleDateRangePicker from "../../components/CustomComponents/SimpleDateRangePicker/SimpleDateRangePicker";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
} from "../staff/dropdownStyles";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";

const getFirstValidId = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const num = Number(candidate);
    if (Number.isFinite(num) && num > 0) {
      return num;
    }
  }
  return null;
};

const getDefaultFormState = () => ({
  slId: null,
  userName: "",
  leaveType: "",
  startDate: moment().format("YYYY-MM-DD"),
  endDate: moment().format("YYYY-MM-DD"),
  startTime: "09:00",
  endTime: "17:00",
  totalHours: "",
  isPaid: false,
  reason: "",
});

const StaffLeave = () => {
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const authState = useSelector((state) => state.auth);
  const profileData = authState?.profileData;
  const userData = authState?.user?.userData;
  const rawCompID =
    userData?.compID ??
    userData?.CompID ??
    profileData?.compID ??
    profileData?.CompID ??
    null;
  const CompID = rawCompID ? Number(rawCompID) : null;
  const loggedInUser =
    userData?.userName ||
    userData?.UserName ||
    userData?.email ||
    "BackOffice";

  const [loading, setLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffDetailsMap, setStaffDetailsMap] = useState({});
  const [staffScheduleMap, setStaffScheduleMap] = useState({});
  const staffScheduleMapRef = useRef({});
  const [leaves, setLeaves] = useState([]);
  const [filters, setFilters] = useState({
    userName: "",
    startDate: "",
    endDate: "",
  });
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formState, setFormState] = useState(getDefaultFormState);
  const [activeLeave, setActiveLeave] = useState(null);
  const [editingLeaveId, setEditingLeaveId] = useState(null);
 

  useEffect(() => {
    staffScheduleMapRef.current = staffScheduleMap;
  }, [staffScheduleMap]);

  const currencyFormatter = useMemo(() => {
    const normalizeCurrency = (code) => {
      if (!code) return "GBP";
      const upper = code.toString().trim().toUpperCase();
      const map = {
        USD: "USD",
        EUR: "EUR",
        GBP: "GBP",
        PKR: "PKR",
      };
      if (map[upper]) return map[upper];
      // handle symbols (€, £, etc.) by mapping to default
      if (upper === "€") return "EUR";
      if (upper === "£") return "GBP";
      if (upper === "₨" || upper === "RS") return "PKR";
      // fallback if not 3-letter
      return upper.length === 3 ? upper : "GBP";
    };

    const compCurrency = normalizeCurrency(profileData?.compCurrency);
    const locale =
      compCurrency === "USD"
        ? "en-US"
        : compCurrency === "EUR"
          ? "en-EU"
          : compCurrency === "PKR"
            ? "en-PK"
            : "en-GB";

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: compCurrency,
        minimumFractionDigits: 2,
      });
    } catch (error) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
      });
    }
  }, [profileData]);

  const formatCurrency = (amount) => {
    const numeric = Number(amount || 0);
    return currencyFormatter.format(isNaN(numeric) ? 0 : numeric);
  };

  const normalizeLeave = useCallback(
    (item) => {

      return {
        SL_ID: item.sL_ID,
        CompID: item.CompID ?? item.compID ?? CompID,
        UserName: item.UserName ?? item.userName ?? "",
        LeaveType: item.LeaveType ?? item.leaveType ?? "",
        StartDate: item.StartDate ?? item.startDate,
        EndDate: item.EndDate ?? item.endDate,
        TotalHours: item.TotalHours ?? item.totalHours ?? 0,
        SalaryType: item.SalaryType ?? item.salaryType ?? "Hourly",
        HourlyRateOrSalary:
          item.HourlyRateOrSalary ?? item.hourlyRateOrSalary ?? item.rate ?? 0,
        IsPaid: item.IsPaid ?? item.isPaid ?? false,
        Reason: item.Reason ?? item.reason ?? "",
        Payout_ID: item.Payout_ID ?? item.payout_ID ?? item.payoutId ?? null,
        CreationDate: item.CreationDate ?? item.creationDate,
        CreatedBy: item.CreatedBy ?? item.createdBy ?? "",
        UpdateDateTime: item.UpdateDateTime ?? item.updateDateTime,
        UpdateBy: item.UpdateBy ?? item.updateBy ?? "",
      };
    },
    [CompID]
  );

  const normalizeWeeklySchedule = useCallback((scheduleList) => {
    if (!Array.isArray(scheduleList)) return [];
    return scheduleList
      .map((slot) => {
        const day =
          (slot.dayName || slot.DayName || slot.Day || slot.day || "")
            .toLowerCase()
            .trim();
        const openTime =
          slot.openTime ||
          slot.OpenTime ||
          slot.startTime ||
          slot.StartTime ||
          null;
        const closeTime =
          slot.closeTime ||
          slot.CloseTime ||
          slot.endTime ||
          slot.EndTime ||
          null;
        const breakMinutes =
          slot.breakMinutes ??
          slot.BreakMinutes ??
          slot.breakDuration ??
          slot.BreakDuration ??
          slot.breakMins ??
          null;
        const isBreakInclusive =
          slot.isBreakInclusive ?? slot.IsBreakInclusive ?? false;

        return {
          day,
          openTime,
          closeTime,
          breakMinutes:
            typeof breakMinutes === "number"
              ? breakMinutes
              : breakMinutes
                ? Number(breakMinutes)
                : null,
          isBreakInclusive,
        };
      })
      .filter((slot) => slot.day && slot.openTime && slot.closeTime);
  }, []);

  const fetchStaffSchedule = useCallback(
    async (userName) => {
      if (!CompID || !userName) return [];
      if (staffScheduleMapRef.current[userName]) {
        return staffScheduleMapRef.current[userName];
      }

      try {
        const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
          params: {
            CompID,
            UserName: userName,
          },
        });

        const staff = response.data?.data || response.data;
        const normalizedSchedule = normalizeWeeklySchedule(
          staff?.weeklySchedule || staff?.WeeklySchedule || []
        );

        setStaffScheduleMap((prev) => ({
          ...prev,
          [userName]: normalizedSchedule,
        }));

        return normalizedSchedule;
      } catch (error) {
        console.error("Failed to load staff schedule", error);
        return [];
      }
    },
    [CompID, normalizeWeeklySchedule]
  );



  
  const fetchStaffOptions = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID,
          SortBy: "First_Name",
          SortOrder: "ASC",
          Search: "",
          PageSize: 500,
          PageNumber: 1,
        },
      });

      const staffData = response.data?.data || response.data || [];
      const mappedDetails = {};
      const mapped =
        Array.isArray(staffData) && staffData.length
          ? staffData
            .filter((staff) => !!staff)
            .map((staff) => {
              const value = staff.userName || staff.UserName || staff.Email;
              if (value) {
                mappedDetails[value] = staff;
              }
              return {
                value,
                label:
                  `${staff.firstName || staff.FirstName || ""} ${staff.lastName || staff.LastName || ""
                    }`.trim() || staff.email || staff.userName || "Unknown",
              };
            })
          : [];
      setStaffOptions(mapped);
      setStaffDetailsMap(mappedDetails);
    } catch (error) {
      console.error("Failed to load staff list", error);
    }
  }, [CompID]);




  const fetchLeaves = useCallback(async () => {
    if (!CompID) return;
    setLoading(true);
    try {
      const params = {
        compId: CompID,
        userName: filters.userName || null,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
      };
   
      const response = await axiosInstance.get(APIs.GET_STAFF_LEAVES, {
        params,
      });
 

      const data = response.data?.data || response.data || [];
      const parsed = Array.isArray(data)
        ? data.filter(Boolean).map((leave) => normalizeLeave(leave))
        : [];

      setLeaves(parsed);
    } catch (error) {
      console.error(error);
  
      showErrorToast("Failed to load staff leaves.");
    } finally {
      setLoading(false);
    }
  }, [CompID, filters, normalizeLeave]);

  useEffect(() => {
    fetchStaffOptions();
  }, [fetchStaffOptions]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  useEffect(() => {
    if (formState.userName) {
      fetchStaffSchedule(formState.userName);
    }
  }, [formState.userName, fetchStaffSchedule]);

  const clearFilters = () => {
    setFilters({
      userName: "",
      startDate: "",
      endDate: "",
    });
    fetchLeaves();
  };

  const openCreateForm = () => {
    setFormState(getDefaultFormState());
    setIsEditing(false);
    setActiveLeave(null);
    setEditingLeaveId(null);
    setIsFormVisible(true);
  };

  const openEditForm = (leave) => {

    setActiveLeave(leave);
    setEditingLeaveId(leave.sL_ID);
    setFormState({
      slId: leave.sL_ID,
      userName: leave.UserName,
      leaveType: leave.LeaveType,
      startDate: leave.StartDate
        ? moment(leave.StartDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      endDate: leave.EndDate
        ? moment(leave.EndDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      startTime: leave.StartDate
        ? moment(leave.StartDate).format("HH:mm")
        : "09:00",
      endTime: leave.EndDate ? moment(leave.EndDate).format("HH:mm") : "17:00",
      totalHours: leave.TotalHours,
      isPaid: !!leave.IsPaid,
      reason: leave.Reason || "",
    });
    setIsEditing(true);
    setIsFormVisible(true);
  };

  const closeForm = () => {
    setIsFormVisible(false);
    setIsEditing(false);
    setFormState(getDefaultFormState());
    setActiveLeave(null);
    setEditingLeaveId(null);
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const buildMomentFromDateTime = (date, time) => {
    if (!date) return null;
    const dateMoment = moment(date, "YYYY-MM-DD", true);
    if (!dateMoment.isValid()) return null;

    if (time) {
      const [hours, minutes] = time.split(":").map((part) => Number(part) || 0);
      dateMoment.hour(hours).minute(minutes).second(0).millisecond(0);
    } else {
      dateMoment.startOf("day");
    }

    return dateMoment;
  };

  const parseTimeOnDay = (dayMoment, timeString) => {
    if (!dayMoment || !timeString) return null;
    const [hours, minutes] = timeString.split(":").map((part) => Number(part) || 0);
    return dayMoment.clone().hour(hours).minute(minutes).second(0).millisecond(0);
  };

  const calculateScheduleMinutes = (schedule, rangeStart, rangeEnd) => {
    if (!schedule?.length) return null;
    let totalMinutes = 0;
    const startDay = rangeStart.clone().startOf("day");
    const endDay = rangeEnd.clone().startOf("day");
    let cursor = startDay.clone();

    while (cursor.isSameOrBefore(endDay, "day")) {
      const dayName = cursor.format("dddd").toLowerCase();
      const daySlots = schedule.filter((slot) => slot.day === dayName);

      for (const slot of daySlots) {
        const slotStart = parseTimeOnDay(cursor, slot.openTime);
        let slotEnd = parseTimeOnDay(cursor, slot.closeTime);
        if (!slotStart || !slotEnd) {
          continue;
        }

        if (!slotEnd.isAfter(slotStart)) {
          slotEnd.add(1, "day");
        }

        const overlapStart = moment.max(slotStart, rangeStart);
        const overlapEnd = moment.min(slotEnd, rangeEnd);

        if (overlapEnd.isAfter(overlapStart)) {
          let minutes = overlapEnd.diff(overlapStart, "minutes");

          if (
            !slot.isBreakInclusive &&
            slot.breakMinutes &&
            overlapStart.isSame(slotStart) &&
            overlapEnd.isSame(slotEnd)
          ) {
            minutes -= slot.breakMinutes;
          }

          totalMinutes += Math.max(minutes, 0);
        }
      }

      cursor.add(1, "day");
    }

    return totalMinutes >= 0 ? totalMinutes : null;
  };

  const calculateRangeMinutes = (rangeStart, rangeEnd) => {
    const diffMinutes = rangeEnd.diff(rangeStart, "minutes");
    return diffMinutes > 0 ? diffMinutes : null;
  };

  const calculateTotalHours = (
    userName,
    startDate,
    startTime,
    endDate,
    endTime,
    scheduleOverride
  ) => {
    const startMoment = buildMomentFromDateTime(startDate, startTime);
    const endMoment = buildMomentFromDateTime(endDate, endTime);

    if (!startMoment || !endMoment) return null;
    if (!endMoment.isSameOrAfter(startMoment)) return null;

    const schedule =
      scheduleOverride ||
      staffScheduleMapRef.current[userName] ||
      staffScheduleMap[userName] ||
      [];

    let minutes = null;
    if (schedule.length) {
      minutes = calculateScheduleMinutes(schedule, startMoment, endMoment);
    }

    if (minutes === null) {
      minutes = calculateRangeMinutes(startMoment, endMoment);
    }

    return minutes !== null ? Number((minutes / 60).toFixed(2)) : null;
  };

  const validateForm = (computedTotalHours) => {
    if (!formState.userName) {
      showErrorToast("Please select a staff member.");
      return false;
    }
    if (!formState.leaveType.trim()) {
      showErrorToast("Leave type is required.");
      return false;
    }
    if (
      moment(formState.endDate).isBefore(moment(formState.startDate))
    ) {
      showErrorToast("End date cannot be before start date.");
      return false;
    }
    if (computedTotalHours === null) {
      showErrorToast("Total hours is required.");
      return false;
    }
    return true;
  };

  const getStaffFinancialDetails = (userName) => {
    const staffInfo = staffDetailsMap[userName];
    const salaryTypeFromStaff =
      staffInfo?.salaryType ||
      staffInfo?.SalaryType ||
      (staffInfo?.isSalaryStaff ? "Salary" : "Hourly");

    const hourlyRateOrSalaryFromStaff =
      salaryTypeFromStaff === "Hourly"
        ? Number(
          staffInfo?.ratePerHour ??
          staffInfo?.RatePerHour ??
          staffInfo?.HourlyRate ??
          0
        )
        : Number(staffInfo?.salary ?? staffInfo?.Salary ?? 0);

    const isPaidFromStaff =
      staffInfo?.isSalaryStaff ?? staffInfo?.IsSalaryStaff ?? false;

    return {
      salaryType: salaryTypeFromStaff || "Hourly",
      hourlyRateOrSalary: hourlyRateOrSalaryFromStaff,
      isPaid: isPaidFromStaff,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const effectiveCompId =
      CompID ??
      activeLeave?.CompID ??
      profileData?.compID ??
      profileData?.CompID ??
      null;

    if (!effectiveCompId) {
      showErrorToast("Company information is missing. Please reload the page.");
      return;
    }
    const scheduleForUser = await fetchStaffSchedule(formState.userName);
    const computedTotalHours = calculateTotalHours(
      formState.userName,
      formState.startDate,
      formState.startTime,
      formState.endDate,
      formState.endTime,
      scheduleForUser
    );

    if (!validateForm(computedTotalHours)) return;

    const startDateTime = buildMomentFromDateTime(
      formState.startDate,
      formState.startTime
    )?.format("YYYY-MM-DDTHH:mm:ss");
    const endDateTime = buildMomentFromDateTime(
      formState.endDate,
      formState.endTime
    )?.format("YYYY-MM-DDTHH:mm:ss");

    const { salaryType, hourlyRateOrSalary, isPaid: staffPaid } =
      getStaffFinancialDetails(formState.userName);

    const resolvedIsPaid =
      typeof formState.isPaid === "boolean"
        ? formState.isPaid
        : typeof staffPaid === "boolean"
          ? staffPaid
          : typeof activeLeave?.IsPaid === "boolean"
            ? activeLeave.IsPaid
            : false;

    const nowUtc = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
    const creationDateValue = isEditing
      ? activeLeave?.CreationDate
        ? moment(activeLeave.CreationDate).utc().format("YYYY-MM-DDTHH:mm:ss")
        : nowUtc
      : nowUtc;

    const resolvedSlId =
      getFirstValidId(
        editingLeaveId,
        formState.slId,
        activeLeave?.SL_ID,
        activeLeave?.sl_ID,
        activeLeave?.SLId,
        activeLeave?.slId,
        activeLeave?.id,
        activeLeave?.Id,
        activeLeave?.ID
      ) ?? 0;

    if (isEditing && !resolvedSlId) {
      showErrorToast(
        "Unable to determine which leave to update. Please close and reopen the form."
      );
      
      return;
    }

    const payload = {
      SL_ID: resolvedSlId,
      CompID: Number(effectiveCompId),
      compID: Number(effectiveCompId),
      UserName: formState.userName,
      StartDate: startDateTime || formState.startDate,
      EndDate: endDateTime || formState.endDate,
      TotalHours: Number(computedTotalHours || 0),
      SalaryType: salaryType || activeLeave?.SalaryType || "Hourly",
      HourlyRateOrSalary:
        typeof hourlyRateOrSalary === "number"
          ? hourlyRateOrSalary
          : Number(activeLeave?.HourlyRateOrSalary ?? 0),
      IsPaid: resolvedIsPaid,
      LeaveType: formState.leaveType,
      Reason: formState.reason,
      Payout_ID: null,
      CreatedBy: loggedInUser,
      CreationDate: creationDateValue,
      UpdateBy: loggedInUser,
      UpdateDateTime: nowUtc,
    };


    try {
      setLoading(true);

      const endpoint = isEditing
        ? APIs.UPDATE_STAFF_LEAVE(resolvedSlId)
        : APIs.CREATE_STAFF_LEAVE;
      
      const response = isEditing
        ? await axiosInstance.put(endpoint, payload)
        : await axiosInstance.post(endpoint, payload);
      

      if (response.data?.status) {
        showSuccessToast(
          isEditing ? "Leave updated successfully." : "Leave created successfully."
        );
        closeForm();
        fetchLeaves();
      } else {
        showErrorToast(response.data?.message || "Unable to save leave.");
      }
    } catch (error) {
      console.error(error);
      
      showErrorToast("Failed to save leave. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (leave) => {
    if (!CompID || !leave?.SL_ID) return;
    const confirmDelete = await swal({
      title: "Delete leave?",
      text: `Are you sure you want to delete leave for ${leave.UserName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      setLoading(true);
      const endpoint = APIs.DELETE_STAFF_LEAVE(leave.SL_ID);
      const params = { compId: CompID };

      const response = await axiosInstance.delete(endpoint, { params });
      
      if (response.data?.status) {
        showSuccessToast("Leave deleted successfully.");
        fetchLeaves();
      } else {
        showErrorToast(response.data?.message || "Unable to delete leave.");
      }
    } catch (error) {
      console.error(error);
 
      showErrorToast("Failed to delete leave.");
    } finally {
      setLoading(false);
    }
  };

  const renderLeaveRows = () => {
    if (!leaves.length) {
      return (
        <tr>
          <td colSpan="9" className="text-center py-4">
            <i className="fa fa-calendar-times fa-2x text-muted mb-2 d-block"></i>
            No leave records found.
          </td>
        </tr>
      );
    }

    return leaves.map((leave) => (
      <tr key={leave.SL_ID}>
        <td className="align-middle">
          <div className="fw-semibold">{leave.UserName}</div>
          <div className="text-muted small">{leave.LeaveType || "-"}</div>
        </td>
        <td className="align-middle">
          {moment(leave.StartDate).format("MMM DD, YYYY")} -{" "}
          {moment(leave.EndDate).format("MMM DD, YYYY")}
        </td>
        <td className="align-middle text-center">
          {leave.TotalHours?.toFixed ? leave.TotalHours.toFixed(1) : leave.TotalHours} hrs
        </td>
        <td className="align-middle text-center">
          {leave.IsPaid ? (
            <span className="badge bg-success">Paid</span>
          ) : (
            <span className="badge bg-secondary">Unpaid</span>
          )}
        </td>
        <td className="align-middle text-center">{leave.SalaryType}</td>
        <td className="align-middle text-center">
          {leave.HourlyRateOrSalary
            ? `${formatCurrency(leave.HourlyRateOrSalary)}`
            : "-"}
        </td>
        <td className="align-middle">{leave.Reason || "-"}</td>
        <td className="align-middle text-nowrap">
          {leave.UpdateDateTime
            ? moment(leave.UpdateDateTime).format("MMM DD, YYYY HH:mm")
            : "-"}
          <div className="text-muted small">{leave.UpdateBy || ""}</div>
        </td>
        <td className="align-middle text-end">
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={() => openEditForm(leave)}
          >
            <i className="fa fa-edit me-1"></i>
            Edit
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(leave)}
          >
            <i className="fa fa-trash me-1"></i>
            Delete
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h1 className="page-header mb-1">Staff Leave</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">Staff</li>
            <li className="breadcrumb-item active">Leave</li>
          </ol>
        </div>
        <button className="btn btn-primary" onClick={openCreateForm}>
          <i className="fa fa-plus me-1"></i>
          Add Leave
        </button>
      </div>

      <div className="mb-4" style={{ position: "relative", zIndex: 2 }}>
        <div className="card filter-card" style={{ position: "relative", zIndex: 2 }}>
          <div className="card-body p-4">
            <div
              className="filter-header"
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="fa fa-filter me-2"></i>
                  <h6 className="mb-0 fw-semibold">Filter Staff Leaves</h6>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="clear-btn"
                    onClick={clearFilters}
                    title="Clear all filters"
                    style={{ height: "32px" }}
                  >
                    <i className="fa fa-times me-1"></i>
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-xl-4 col-lg-4 col-md-6">
                <label className="filter-label">
                  <i className="fa fa-user"></i>
                  Select Staff
                </label>
                <div className="filter-control">
                  <Select
                    value={
                      filters.userName
                        ? staffOptions.find(
                          (staff) => staff.value === filters.userName
                        ) || null
                        : null
                    }
                    onChange={(option) =>
                      setFilters((prev) => ({
                        ...prev,
                        userName: option?.value || "",
                      }))
                    }
                    options={staffOptions}
                    styles={dropdownStyles}
                    placeholder="All Staff"
                    className="filter-dropdown"
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    closeMenuOnScroll={false}
                  />
                </div>
              </div>

              <div className="col-xl-4 col-lg-4 col-md-6">
                <label className="filter-label">
                  <i className="fa fa-calendar"></i>
                  Date Range
                </label>
                <div className="filter-control">
                  <SimpleDateRangePicker
                    startDate={
                      filters.startDate && moment(filters.startDate).isValid()
                        ? moment(filters.startDate).toDate()
                        : null
                    }
                    endDate={
                      filters.endDate && moment(filters.endDate).isValid()
                        ? moment(filters.endDate).toDate()
                        : null
                    }
                    onChange={([start, end]) => {
                      setFilters((prev) => ({
                        ...prev,
                        startDate: start ? moment(start).format("YYYY-MM-DD") : "",
                        endDate: end ? moment(end).format("YYYY-MM-DD") : "",
                      }));
                    }}
                    placeholder="Select Date Range"
                    className="filter-dropdown"
                  />
                </div>
              </div>
            </div>

            <div className="bottom-controls">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center">
                    <span className="control-label mb-0">
                      <i className="fa fa-list"></i>
                      Showing:
                    </span>
                    <span className="stats-text">
                      {leaves.length} leave record{leaves.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    className="refresh-btn"
                    onClick={fetchLeaves}
                    disabled={loading}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-sync-alt"
                        } me-1`}
                    ></i>
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className={isDarkMode ? "bg-dark text-white" : "bg-light"}>
                <tr>
                  <th>Staff</th>
                  <th>Period</th>
                  <th className="text-center">Hours</th>
                  <th className="text-center">Paid?</th>
                  <th className="text-center">Salary Type</th>
                  <th className="text-center">Rate</th>
                  <th>Reason</th>
                  <th>Last Updated</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>{renderLeaveRows()}</tbody>
            </table>
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={isFormVisible}
        onClose={closeForm}
        title={isEditing ? "Edit Leave" : "Add Leave"}
        modalSize="modal-lg"
        isCentered
        showButton={false}
      >
        <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-6">
            <label className="form-label">
              Staff<span className="text-danger">*</span>
            </label>
            <Select
              value={
                formState.userName
                  ? staffOptions.find(
                    (staff) => staff.value === formState.userName
                  ) || null
                  : null
              }
              onChange={(option) => {
                const value = option?.value || "";
                const staffInfo = value ? staffDetailsMap[value] : null;
                const staffPaid =
                  staffInfo?.isSalaryStaff ?? staffInfo?.IsSalaryStaff;
                setFormState((prev) => ({
                  ...prev,
                  userName: value,
                  isPaid:
                    typeof staffPaid === "boolean" ? staffPaid : prev.isPaid,
                }));
              }}
              options={staffOptions}
              styles={dropdownStyles}
              placeholder="Select staff"
              className="filter-dropdown"
              isClearable
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">
              Leave Type<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="leaveType"
              value={formState.leaveType}
              onChange={handleFormChange}
              placeholder="Sick, Vacation, etc."
              required
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Start Date &amp; Time</label>
            <div className="row g-2">
              <div className="col-6">
                <input
                  type="date"
                  className="form-control"
                  name="startDate"
                  value={formState.startDate}
                  onChange={handleFormChange}
                />
              </div>
              <div className="col-6">
                <input
                  type="time"
                  className="form-control"
                  name="startTime"
                  value={formState.startTime || ""}
                  onChange={handleFormChange}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label">End Date &amp; Time</label>
            <div className="row g-2">
              <div className="col-6">
                <input
                  type="date"
                  className="form-control"
                  name="endDate"
                  value={formState.endDate}
                  onChange={handleFormChange}
                />
              </div>
              <div className="col-6">
                <input
                  type="time"
                  className="form-control"
                  name="endTime"
                  value={formState.endTime || ""}
                  onChange={handleFormChange}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label d-block">Paid Leave</label>
            <ToggleSwitch
              name="isPaid"
              isChecked={!!formState.isPaid}
              onToggle={(checked) =>
                setFormState((prev) => ({
                  ...prev,
                  isPaid: checked,
                }))
              }
              colorTheme="green"
              className="mt-1"
            />
            <small className="text-muted">
              Toggle to mark if this leave should be paid.
            </small>
          </div>
          <div className="col-12">
            <label className="form-label">Reason</label>
            <textarea
              className="form-control"
              rows="3"
              name="reason"
              value={formState.reason}
              onChange={handleFormChange}
              placeholder="Optional notes about this leave"
            ></textarea>
          </div>
          <div className="col-12 d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-light" onClick={closeForm}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditing ? "Update Leave" : "Create Leave"}
            </button>
          </div>
        </form>
      </CustomModal>
    </div>
  );
};

export default StaffLeave;

